import { useCallback, useEffect, useRef, useState } from "react";

/**
 * ImageLightbox (responsive + gestures)
 * - images: array de string sau { src, alt }
 * - index, isOpen, onClose, onPrev, onNext
 */
export default function ImageLightbox({
  images = [],
  index = 0,
  isOpen,
  onClose,
  onPrev,
  onNext,
}) {
  const [i, setI] = useState(index);
  const wrapRef = useRef(null);
  const touchStartX = useRef(0);
  const touchDeltaX = useRef(0);

  useEffect(() => setI(index), [index]);

  const norm = (it) => (typeof it === "string" ? { src: it, alt: "" } : it || {});
  const { src, alt = "" } = norm(images[i] ?? {});

  const handlePrev = useCallback(() => {
    if (images.length < 2) return;
    setI((v) => (v - 1 + images.length) % images.length);
    onPrev?.();
  }, [images.length, onPrev]);

  const handleNext = useCallback(() => {
    if (images.length < 2) return;
    setI((v) => (v + 1) % images.length);
    onNext?.();
  }, [images.length, onNext]);

  const escHandler = useCallback(
    (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowLeft") handlePrev();
      if (e.key === "ArrowRight") handleNext();
    },
    [handlePrev, handleNext, onClose]
  );

  // lock body scroll + key handlers while open
  useEffect(() => {
    if (!isOpen) return;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    window.addEventListener("keydown", escHandler);
    return () => {
      window.removeEventListener("keydown", escHandler);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, escHandler]);

  if (!isOpen) return null;

  // touch gestures (swipe)
  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchDeltaX.current = 0;
  };
  const onTouchMove = (e) => {
    touchDeltaX.current = e.touches[0].clientX - touchStartX.current;
  };
  const onTouchEnd = () => {
    const THRESHOLD = 50; // px
    if (touchDeltaX.current > THRESHOLD) handlePrev();
    else if (touchDeltaX.current < -THRESHOLD) handleNext();
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      <div
        ref={wrapRef}
        className="relative w-full h-full sm:h-auto sm:w-auto sm:max-w-[92vw] sm:max-h-[92vh] flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <img
          src={src}
          alt={alt}
          className="max-h-[88vh] max-w-[98vw] sm:max-h-[92vh] sm:max-w-[92vw] object-contain rounded-none sm:rounded-xl shadow-2xl"
          draggable="false"
        />

        {/* Close */}
        <button
          onClick={onClose}
          aria-label="Închide"
          className="absolute top-4 right-4 sm:-top-3 sm:-right-3 rounded-full bg-white text-gray-900 p-3 sm:p-2 shadow focus:outline-none focus:ring-2 focus:ring-white"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M6 6l12 12M6 18L18 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>

        {/* Arrows / hit areas */}
        {images.length > 1 && (
          <>
            <button
              onClick={handlePrev}
              aria-label="Înapoi"
              className="absolute left-0 top-0 h-full w-1/3 sm:w-12 sm:h-12 sm:left-[-3rem] sm:top-1/2 sm:-translate-y-1/2
                         bg-transparent sm:bg-white/90 text-white sm:text-gray-900 flex items-center justify-start sm:justify-center"
            >
              <svg className="ml-3 sm:ml-0" width="22" height="22" viewBox="0 0 24 24">
                <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" />
              </svg>
            </button>

            <button
              onClick={handleNext}
              aria-label="Înainte"
              className="absolute right-0 top-0 h-full w-1/3 sm:w-12 sm:h-12 sm:right-[-3rem] sm:top-1/2 sm:-translate-y-1/2
                         bg-transparent sm:bg-white/90 text-white sm:text-gray-900 flex items-center justify-end sm:justify-center"
            >
              <svg className="mr-3 sm:mr-0" width="22" height="22" viewBox="0 0 24 24">
                <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" />
              </svg>
            </button>
          </>
        )}

        {/* Caption */}
        {alt && (
          <div className="absolute left-0 right-0 bottom-4 text-center text-white/90 text-sm px-4">
            {alt}
          </div>
        )}
      </div>
    </div>
  );
}
